/**
 * Shared foundation for the four polymorphic Trade Organization
 * dashboards (producer / worker / writer / general).
 *
 * Segregation-first: every variant lives in its own folder under
 * ./variants/ and imports THIS shell (never each other). Shared
 * widgets sit in ./widgets/.
 *
 * The shell composes the *universal* Trade-Org governance surface
 * (term countdown · iCard issue · committee roster · 3-OTP proposals
 * inbox · on-the-job live count · member directory) so every variant
 * inherits them for free. Variant-specific panels render inside the
 * `variant slot` between the governance block and the directory.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import VerificationGate from "@/components/VerificationGate";
import QuorumStatusBanner from "./widgets/QuorumStatusBanner";
import CommitteeInvitePanel from "./widgets/CommitteeInvitePanel";
import TermBanner from "./widgets/TermBanner";
import OnTheJobPanel from "./widgets/OnTheJobPanel";
import CommitteeGovernancePanel from "./widgets/CommitteeGovernancePanel";
import MemberDirectoryPanel from "./widgets/MemberDirectoryPanel";
import MembershipTiersPanel from "./widgets/MembershipTiersPanel";
import MeetingRoomsPanel from "./widgets/MeetingRoomsPanel";
import FederationLedgerPanel from "./widgets/FederationLedgerPanel";
import DeclaredAffiliatesPanel from "./widgets/DeclaredAffiliatesPanel";
import OrgTeamPanel from "@/components/OrgTeamPanel";

export default function TradeOrgShell({ variantName, accent, children }) {
  const [org, setOrg]                 = useState(null);
  const [quorum, setQuorum]           = useState(null);
  const [features, setFeatures]       = useState({});
  const [loading, setLoading]         = useState(true);
  const [tiersVersion, setTiersVersion] = useState(0);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: orgResp } = await api.get("/onboard/me/organization");
      if (!orgResp.organization) return;
      const oid = orgResp.organization.id;
      // Await the full parallel set BEFORE committing state so the
      // initial paint doesn't flash a half-loaded shell (QuorumBanner
      // returning null before quorum-verify resolves).
      const [q, f] = await Promise.all([
        api.get(`/orgs/${oid}/committee/quorum-verify`).catch(() => ({ data: null })),
        api.get(`/orgs/${oid}/features`).catch(() => ({ data: { features: {} } })),
      ]);
      setOrg(orgResp.organization);
      setQuorum(q.data);
      setFeatures(f.data?.features || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  if (loading && !org) {
    return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55" data-testid="trade-org-loading">Loading…</div>;
  }
  if (!org) {
    return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55">No organization found.</div>;
  }

  // Non-association users should never hit this shell — the router
  // dispatches on account_type first — but guard anyway.
  if (org.type !== "association") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <div className="cs-card rounded-2xl p-10">
          <div className="font-display text-2xl uppercase tracking-tight">Trade Org Only</div>
          <p className="mt-3 text-sm text-white/65">Your org is registered as &ldquo;{org.type}&rdquo;.</p>
        </div>
      </div>
    );
  }

  // KYC gate — bylaws + certificate + reg number
  if (!org.verified) {
    return <VerificationGate org={org} onVerified={(updated) => setOrg(updated)} />;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid={`trade-org-${variantName.toLowerCase()}-dashboard`}>
      <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accent }}
           data-testid="variant-tag">
        {variantName} Trade Organization
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          data-testid="org-brand-name">
        {org.brand_name}
      </h1>
      <div className="mt-1 text-sm text-white/55">
        {org.legal_name} · {org.association_status === "active"
          ? <span className="text-emerald-300">Active</span>
          : <span className="text-amber-300">Provisional</span>}
      </div>

      {/* Universal governance surface (all variants inherit) */}
      <QuorumStatusBanner quorum={quorum} accent={accent} />
      <TermBanner org={org} onChange={refresh} accent={accent} />
      <OnTheJobPanel org={org} />

      <div className="mt-8 space-y-6">
        <CommitteeInvitePanel org={org} onChange={refresh} accent={accent} />
        <CommitteeGovernancePanel org={org} onChange={refresh} accent={accent} />
      </div>

      {/* Variant-specific slot */}
      <div className="mt-8" data-testid="variant-slot">
        {typeof children === "function"
          ? children({ org, quorum, features, refresh })
          : children}
      </div>

      {/* Federation surface (all variants) — meeting rooms broadcast
          + video conference + tier CRUD + federated ledger */}
      <div className="mt-8 space-y-6" data-testid="federation-surface">
        <MeetingRoomsPanel org={org} accent={accent} />
        <MembershipTiersPanel org={org} accent={accent}
                              onChange={() => setTiersVersion((v) => v + 1)} />
      </div>

      {/* Member directory (all variants) — now tier-filtered */}
      <div className="mt-8">
        <MemberDirectoryPanel org={org} tiersVersion={tiersVersion} />
      </div>

      {/* Declared Affiliates — reverse-lookup of everyone who ticked
          this trade org at signup. Vouch buttons flip a Verified chip
          on the target's public profile. */}
      <div className="mt-8">
        <DeclaredAffiliatesPanel org={org} accent={accent} />
      </div>

      {/* Federated network ledger — every trade org on the network */}
      <div className="mt-8">
        <FederationLedgerPanel accent={accent} />
      </div>

      {/* Back-office staff (paid team) */}
      <div className="mt-8">
        <OrgTeamPanel org={org} />
      </div>
    </div>
  );
}
